import { FiltroDeTitulos, Titulo } from "@/models/financeiro/Titulo";
import { formatarMoeda } from "./formatadores";

export function limparFiltroDosCamposDeTitulos(): FiltroDeTitulos {
	return {
		pessoa: '',
		tipoDeCliente: '',
		dataInicioPeriodo: '',
		dataFimPeriodo: '',
		formaDePagamento: '',
		numeroDocumento: '',
		dataInicioPagamento: '',
		dataFimPagamento: '',
		somenteComValorDesatualizado: false,
		somenteComErroDeRecalculo: false,
	}
}

export function calcularDataAposCarencia(titulo: Titulo, dataVencimento: Date): Date {
	const diasDeCarencia = titulo.diasDeCarencia || 0;

	const dataCarencia = new Date(
		dataVencimento.getFullYear(),
		dataVencimento.getMonth(),
		dataVencimento.getDate(),
	);

	if (diasDeCarencia > 0) {
		dataCarencia.setDate(dataCarencia.getDate() + diasDeCarencia);
	} else {
		const diaSemanaVencimento = dataCarencia.getDay();
		if (diaSemanaVencimento === 6) {
			dataCarencia.setDate(dataCarencia.getDate() + 2);
		} else if (diaSemanaVencimento === 0) {
			dataCarencia.setDate(dataCarencia.getDate() + 1);
		}
	}

	return new Date(
		dataCarencia.getFullYear(),
		dataCarencia.getMonth(),
		dataCarencia.getDate(),
	);
}


/**
 * Diz se o título ainda tem saldo a receber, ou seja, se ele pode entrar num pagamento.
 *
 * Este é o predicado ÚNICO da regra: ele decide quem aparece na grid do pagamento múltiplo,
 * quem pode nascer pré-selecionado e quem sobrevive quando a seleção é reconciliada depois
 * de uma busca. As três coisas precisam usar o mesmo critério, senão um título quitado some
 * da tela mas continua selecionado por baixo, soma no total e acaba recebendo uma baixa de
 * R$ 0,00.
 *
 * O saldo é o critério, e não a situação do título: existe título marcado como pago que
 * ainda tem saldo em aberto.
 *
 * Recebe o título BRUTO, nunca o formatado para exibição, cujos valores já são string.
 */
export function tituloTemSaldoAPagar(titulo: { valorAtualizado?: any; valor?: any }): boolean {
	return Number(titulo.valorAtualizado ?? titulo.valor) > 0
}


/**
 * Soma o saldo que os títulos marcados no Pagamento Múltiplo ainda devem.
 *
 * Cada item da seleção carrega o saldo já formatado para exibição ("R$ 1.234,56"), recalculado
 * para a data de pagamento escolhida. Esta soma é o teto do pagamento, então o desmonte da
 * máscara precisa ser exato: perder o separador de milhar aqui alarga a trava em mil vezes.
 */
export function somarSaldoDosTitulosSelecionados(
	titulosSelecionados: { valorAtualizado?: any }[],
): number {
	const soma = titulosSelecionados.reduce((total, titulo) => {
		const saldo = titulo.valorAtualizado
		// O ramo numérico não é firula: passar um number pelo desmonte da máscara faria o regex
		// comer o ponto decimal e 59.2 viraria 592, alargando o teto em dez vezes sem erro nenhum.
		const valor = typeof saldo === 'number'
			? saldo
			: parseFloat(String(saldo).replace(/[^\d,-]/g, '').replace(',', '.'))
		return total + (isNaN(valor) ? 0 : valor)
	}, 0)

	return Number.parseFloat(soma.toFixed(2))
}


/**
 * Valida o campo "Valor a Pagar" do Pagamento Múltiplo contra o saldo dos títulos selecionados.
 *
 * O teto é o saldo NA DATA DE PAGAMENTO informada, e não o saldo de hoje. Numa baixa retroativa
 * os encargos do período que ainda não passou deixam de existir, o saldo cai, e o backend só
 * consegue baixar até esse saldo menor: ele distribui o valor em cascata por vencimento, cada
 * título consome até o próprio teto e o que sobra é descartado sem erro nenhum. Foi o que gerou
 * o 39094, em que R$ 64,25 informados viraram R$ 59,20 no comprovante.
 *
 * O valor chega aqui como a STRING formatada em pt-BR do CampoDecimal ("1.234,56"), e não como
 * o número do v-model: quem alimenta as :rules é o v-text-field interno do componente. O ramo
 * numérico existe só para quem chamar isto direto.
 */
export function validarValorDoPagamentoEmMassa(
	valorDigitado: string | number | null | undefined,
	saldoDosTitulos: number,
): true | string {
	if (valorDigitado === null || valorDigitado === undefined || valorDigitado === '') {
		return 'Valor de pagamento é obrigatório'
	}

	const valor = typeof valorDigitado === 'number'
		? valorDigitado
		: parseFloat(valorDigitado.replace(/\./g, '').replace(',', '.'))

	if (isNaN(valor)) return 'Valor de pagamento é obrigatório'

	if (valor > saldoDosTitulos) {
		return `Valor superior ao saldo dos títulos na data (R$ ${formatarMoeda(saldoDosTitulos)})`
	}

	return true
}
